import { InputNumber } from 'antd';
import type { ComponentProps, ReactNode } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { FormField } from '../form-field';

import styles from './number-field.module.scss';

type AntdInputNumberProps = Omit<
  ComponentProps<typeof InputNumber>,
  'value' | 'onChange' | 'defaultValue' | 'prefix' | 'suffix' | 'size'
>;

export type NumberFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = AntdInputNumberProps & {
  name: TName;
  control: Control<TFieldValues>;
  label?: ReactNode;
  help?: string;
  required?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  fieldSize?: 'default' | 'tall';
};

export function NumberField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  label,
  help,
  required,
  prefix,
  suffix,
  fieldSize = 'default',
  className,
  ...rest
}: NumberFieldProps<TFieldValues, TName>) {
  return (
    <FormField name={name} control={control} label={label} help={help} required={required}>
      {({ field, fieldState }) => (
        <InputNumber
          id={name}
          size={fieldSize === 'tall' ? 'large' : 'middle'}
          status={fieldState.error ? 'error' : undefined}
          controls={false}
          prefix={prefix}
          suffix={suffix}
          className={[styles.number, className].filter(Boolean).join(' ')}
          {...rest}
          value={field.value ?? null}
          onChange={field.onChange}
          onBlur={field.onBlur}
          ref={field.ref}
        />
      )}
    </FormField>
  );
}
